import React, { useState } from 'react';
import parse from 'html-react-parser';
import GoogleMapReact from 'google-map-react';
import { capitalCities } from '../../../utils/constants';
import generalController from '../../../controllers/generalController';
import Input from '../../1_Atoms/Forms/Input/Input';
import Button from '../../1_Atoms/Button/Button';
import { Svg } from '../../1_Atoms/Svg/Svg';
import { languageController } from '../../../controllers/language/languageController';
import { convertPostalToCoordinates, getPreviousUserlocation, getUserGeoLocation, storeUserLocation } from '../../../utils/googleMapHelper';
import formValidationController from '../../../controllers/formValidationController';
import { pushToGTMDataLayer } from '../../../utils/helpers';

interface FormExtraParams {
  introductionText?: string;
  radiusValue: string | number;
  disclaimerText?: string;
}

interface EVRangeMapProps {
  formExtraParams: FormExtraParams;
}

interface Location {
  lat: number;
  lng: number;
}

interface PostalCodeLocationData {
  postalcode: string;
  coordinates: Location;
}

interface StatusMessage {
  text: string;
  isError: boolean;
}

interface EVCircleCenterDotProps extends Location {}

const EVCircleCenterDot: React.FC<EVCircleCenterDotProps> = () => (
  <div className="mz-ev-range-map__center-dot"></div>
);

const EVRangeMap: React.FC<EVRangeMapProps> = ({
  formExtraParams
}) => {
  const { introductionText, radiusValue, disclaimerText } = formExtraParams;
  const [postalCode, setPostalCode] = useState('');
  const [statusMessage, setStatusMessage] = useState<StatusMessage>({ text: '', isError: false });
  const [errorMessage, setErrorMessage] = useState('');
  const [currentCircle, setCurrentCircle] = useState<google.maps.Circle | null>(null);
  const [maps, setMaps] = useState<any>(null);
  const [map, setMap] = useState<any>(null);
  const [userLocation, setUserLocation] = useState<Location>({ lat: 0, lng: 0 });

  const drawCircle = (map: any, maps: any, center: Location): void => {
    if (currentCircle) {
      currentCircle.setMap(null);
    }

    const newCircle = new maps.Circle({
      strokeColor: "#910A2D",
      strokeWeight: 2,
      fillColor: "#910A2D",
      fillOpacity: 0.2,
      map,
      center: center,
      radius: Number(radiusValue) * 1000,
    });

    setCurrentCircle(newCircle);
  };

  const handleAPILoaded = (map: any, maps: any): void => {
    let mapCenter: Location | undefined;
    const prevUserLocationData = getPreviousUserlocation();

    if (prevUserLocationData) {
      if (prevUserLocationData.type === 'geolocation' && typeof prevUserLocationData.value !== 'string') {
        mapCenter = prevUserLocationData.value;
      }
      else if (prevUserLocationData.type === 'postalcode' && typeof prevUserLocationData.value !== 'string') {
        const locationData = prevUserLocationData.value as unknown as PostalCodeLocationData;
        mapCenter = locationData.coordinates;
        setPostalCode(locationData.postalcode);
      }
    }
    else {
      const province = generalController.getMetatagValue('province');
      if (province) {
        const capitalData = capitalCities[province as keyof typeof capitalCities];
        if (capitalData) {
          mapCenter = capitalData.location;
        }
      }
    }

    if (mapCenter) {
      setUserLocation(mapCenter);
      drawCircle(map, maps, mapCenter);
      setMaps(maps);
      setMap(map);
    }
  };

  const onSearchLocation = async (postalCode: string): Promise<void> => {
    try {
      if (!postalCode) return;

      if (formValidationController.validateField(postalCode, 'postalcode')) {
        if (errorMessage) setErrorMessage('');

        setStatusMessage({ text: languageController.getCopy('evRangeMap', 'showRangeLoading') || '', isError: false });

        const res = await convertPostalToCoordinates(postalCode);

        if (res) {
          setUserLocation(res);
          drawCircle(map, maps, res);
          const locationData: PostalCodeLocationData = { postalcode: postalCode, coordinates: res };
          storeUserLocation('postalcode', locationData as any);

          pushToGTMDataLayer({ event: 'Postalcode Search', postalcode: postalCode, form: 'EV Range Map', formid: 'EVRangeMap' });

          setStatusMessage({ isError: false, text: '' });
          return;
        }

        setErrorMessage(languageController.getError('general') || '');
      } else {
        setErrorMessage(languageController.getError('postalcode') || '');
      }
    } catch (err: any) {
      console.log('Error: ', err);
      if (err.response) {
        setErrorMessage(err.response.data?.message || '');
      }
    }
  };

  const onGeolocationSubmit = async (): Promise<void> => {
    setStatusMessage({ text: languageController.getCopy('findDealer', 'geoLoading') || '', isError: false });
    const res = await getUserGeoLocation();

    if (res.success && typeof res.data !== 'string') {
      setStatusMessage({ isError: false, text: '' });
      drawCircle(map, maps, res.data);
      setUserLocation(res.data);
      storeUserLocation('geolocation', res.data);
      pushToGTMDataLayer({ event: 'Current Location Search', result: 'success', location: res.data });
    } else {
      const errorText = typeof res.data === 'string' ? res.data : '';
      setStatusMessage({ text: errorText, isError: true });
      pushToGTMDataLayer({ event: 'Current Location Search', result: 'error', location: null });
      setTimeout(() => {
        setStatusMessage({ isError: false, text: '' });
      }, 3000);
    }
  };

  return (
    <div className="mz-ev-range-map">
      <div className="mz-container">
        <div className="mz-full-width">
          {introductionText && (
            <div className="mz-ev-range-map__intro-text">
              {parse(introductionText)}
            </div>
          )}
          <div className="mz-ev-range-map__search">
            <div className="mz-ev-range-map__search-inner">
              <div className="mz-ev-range-map__input">
                <Input
                  type="text"
                  name="postalcode"
                  label={languageController.getFormCopy('label', 'postalcode')}
                  value={postalCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostalCode(e.target.value)}
                  maxLength={7}
                />
                <Button
                  className="mz-btn--primary"
                  label={languageController.getCopy('evRangeMap', 'searchCTA')}
                  onClick={() => onSearchLocation(postalCode)}
                  data-menu-name="Postalcode Search"
                  data-form-name="EV Range Map"
                  data-form-event="Show range at the postalcode"
                />
              </div>
              {
                errorMessage && (
                  <div className="mz-ev-range-map__error-message">{errorMessage}</div>
                )
              }
              <p className="text--grey paragraph">{languageController.getCopy('findDealer', 'or')}</p>
              <Button
                className="mz-btn--primary mz-ev-range-map__geolocation-btn"
                label={languageController.getCopy('findDealer', 'useGeolocation')}
                onClick={onGeolocationSubmit}
                buttonIcon={<Svg type="locationairplane" size={1.2} colour="white" style={{ top: '1px' }} />}
                buttonIconPosition="right"
                data-menu-name="Geolocation Search"
                data-form-name="EV Range Map"
                data-form-event="Show range at the geolocation"
                />
              <div className={`mz-ev-range-map__status-message ${statusMessage.text ? 'active' : ''}`}>
                <Svg type={statusMessage.isError ? 'warning' : 'sync'} size={1} colour="white" style={{ top: '1px' }} />
                <span className="interactive">{statusMessage.text}</span>
              </div>
            </div>
          </div>
          <div className="mz-ev-range-map__map-widget">
            <GoogleMapReact
              bootstrapURLKeys={{
                key: process.env.REACT_APP_GOOGLE_MAP_KEY || '',
                region: 'ca',
                language: typeof document !== 'undefined' && document.documentElement.lang?.toLowerCase().startsWith('fr') ? 'fr' : 'en'
              }}
              defaultCenter={{ lat: 0, lng: 0 }}
              center={userLocation}
              zoom={10}
              options={{
                minZoom: 6,
                maxZoom: 14,
                fullscreenControl: false
              }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => handleAPILoaded(map, maps)}
            >
              <EVCircleCenterDot
                lat={userLocation.lat}
                lng={userLocation.lng}
              />
            </GoogleMapReact>
          </div>
          {
            disclaimerText && (
              <div className="mz-ev-range-map__disclaimer label text--grey">
                {parse(disclaimerText)}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}

export default EVRangeMap;
